<template>
  <div
    @click.right.stop
    @click.stop
    @drag="isDragging = true"
    @dragend="onDragEnd"
    class="file-preview-list transition-transform duration-150 motion-reduce:transition-none"
    :class="{ 'pointer-events-none translate-x-full': isDragging }"
  >
    <div
      :class="[
        !minimal ? 'border-gray-lighten-1 surface rounded-md border-2 p-2' : '',
        !minimal && !inline ? 'absolute! right-0' : '',
        containerClass,
        dynamicContainerClass
      ]"
      @mouseleave.stop="$emit('leave')"
    >
      <div v-if="!minimal" class="mb-2 flex w-full justify-end sm:hidden">
        <PButton variant="outline" size="sm" color="warning" @click="$emit('close')">
          Annuleren
        </PButton>
      </div>

      <div v-if="$slots.default" class="mb-2 flex w-full text-xs font-bold select-none">
        <slot />
      </div>

      <div
        class="grid auto-rows-fr gap-1"
        :class="[gridClass, dynamicGridClass]"
        v-if="renderComponent"
      >
        <FilePreviewTile
          v-for="(fileRelation, i) in fileRelations"
          :key="fileRelation.id"
          v-model="fileRelations[i]"
          @click="onClickTile(fileRelation)"
          @reload="forceRender"
          @delete="promptDeleteFileRelation"
          @moved="onMove?.(fileRelation)"
          @open="onContextMenuStateChange"
          :drag-default-is-copy="dragDefaultIsCopy"
          :allow-context-menu="allowContextMenu"
          class="h-40"
        />
        <FilePreviewTile v-if="isUploading" class="h-40" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, nextTick, ref, watch, computed } from "vue"
import { FileRelation } from "@/models/file/file-relation.model"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import PButton from "@/components/elements/PButton.vue"
import FilePreviewTile from "@/components/file/FilePreviewTile.vue"
import type { FileController } from "@/controllers/file-controller"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"

const props = withDefaults(
  defineProps<{
    minimal?: boolean
    /** Let the parent overlay position the preview. */
    inline?: boolean
    containerClass?: string
    gridClass?: string
    isUploading?: boolean
    dragDefaultIsCopy?: boolean
    closeOnClick?: boolean
    onDelete?: (fileRelation: FileRelation) => void
    onMove?: (fileRelation: FileRelation) => void
    allowContextMenu?: boolean
  }>(),
  {
    minimal: false,
    inline: false,
    containerClass: "",
    gridClass: "h-full",
    isUploading: false,
    closeOnClick: true,
    allowContextMenu: true
  }
)

const emit = defineEmits(["close", "allImagesLoaded", "open", "leave"])

const fileRelations = defineModel<FileRelation[]>({ required: true })

const isDragging = ref(false)

function onClickTile(fileRelation: FileRelation) {
  FileViewerController.display(fileRelations.value, fileRelation, {
    getArray: () => fileRelations.value,
    onDelete: props.onDelete
  })
  if (props.closeOnClick) {
    emit("close")
  }
}

const fileController: FileController = inject("fileController")!
function promptDeleteFileRelation(fileRelation: FileRelation) {
  const dialogStore = useDialogStore()
  dialogStore.currentDialog = {
    title: "Bestand verwijderen",
    body: `Weet je zeker dat je ${fileRelation.file.originalName} wilt verwijderen?`,
    type: DialogTypes.CONFIRM,
    onConfirm: () => deleteFileRelation(fileRelation),
    textConfirm: "Verwijderen",
    classConfirm: "bg-error! text-on-error!"
  }
}

function deleteFileRelation(fileRelation: FileRelation) {
  fileController.deleteFileRelation(fileRelation, props.onDelete)

  // Delete from local list (necessary for unsaved files, i.e. empty instruction processing row).
  const index = fileRelations.value.findIndex((fr) => fr.id === fileRelation.id)
  if (index !== -1) {
    fileRelations.value.splice(index, 1)
  }
}

// close menu when file lenght is 0
watch(
  () => fileRelations.value.length,
  (newLength) => {
    if (newLength === 0) {
      emit("close")
    }
  }
)

const renderComponent = ref(true)
/* This can be called to re-mount the component, for example then the image is rotated and the preview needs to be updated  */
function forceRender() {
  renderComponent.value = false
  nextTick(() => {
    renderComponent.value = true
  })
}

const dynamicGridClass = computed(() => {
  const itemCount = fileRelations.value.length + (props.isUploading ? 1 : 0)
  if (itemCount === 1) return "grid-cols-1"
  if (itemCount === 2) return "grid-cols-2"
  if (itemCount === 3) return "grid-cols-3"
  return "grid-cols-3" // Default to 3 columns for more than 3 items
})

const dynamicContainerClass = computed(() => {
  if (props.inline) return "w-full"
  const itemCount = fileRelations.value.length + (props.isUploading ? 1 : 0)
  if (itemCount === 1) return "w-1/3 max-w-48"
  if (itemCount === 2) return "w-2/3 max-w-96"
  if (itemCount === 3) return "w-full  max-w-xl"
  return "w-full" // Default to full width for more than 3 items
})

function onDragEnd() {
  isDragging.value = false
  emit("close")
}

function onContextMenuStateChange(isOpen: boolean) {
  emit("open", isOpen)
}
</script>

<style scoped></style>
